import flatpickr from "flatpickr";
import type { Instance as FlatpickrInstance } from "flatpickr/dist/types/instance";
import type { Options } from "flatpickr/dist/types/options";
import "flatpickr/dist/flatpickr.min.css";

type PickerInput = HTMLInputElement & {
  _flatpickr?: FlatpickrInstance;
};

declare global {
  interface Window {
    __WMU_DATETIME_PICKER_BOOTED?: boolean;
    WMUDateTimePicker?: {
      init(root?: ParentNode): void;
    };
  }
}

export type WMUDateTimePickerAPI = {
  init: (root?: ParentNode) => void;
};

const doc = document;
const PICKER_SELECTOR = 'input[type="datetime-local"]:not([data-picker-ignore]), input[type="time"]:not([data-picker-ignore])';

function boot() {
  init(doc);
  doc.body.addEventListener("htmx:afterSwap", (event) => {
    const detail = (event as CustomEvent<{ target?: EventTarget | null }>).detail;
    const target = detail?.target;
    if (target instanceof HTMLElement) {
      init(target);
    }
  });
}

function init(root: ParentNode = doc) {
  if (root instanceof HTMLInputElement && root.matches(PICKER_SELECTOR)) {
    bindPicker(root);
  }
  root.querySelectorAll<PickerInput>(PICKER_SELECTOR).forEach((input) => {
    bindPicker(input);
  });
}

function bindPicker(input: PickerInput) {
  if (!(input instanceof HTMLInputElement)) {
    return;
  }
  if (input.dataset.wmuDatetimePickerBound === "true" || input._flatpickr) {
    return;
  }

  const options = buildOptions(input);
  flatpickr(input, options);
  input.dataset.wmuDatetimePickerBound = "true";

  const instance = input._flatpickr;
  if (instance && input.type === "datetime-local" && typeof input.id === "string" && input.id.trim() !== "") {
    bindExternalLabels(input.id.trim(), instance);
  }
}

function buildOptions(input: PickerInput): Partial<Options> {
  const minuteIncrement = resolveMinuteIncrement(input);
  const common: Partial<Options> = {
    disableMobile: true,
    time_24hr: true,
    allowInput: true,
    minuteIncrement,
    clickOpens: true,
    locale: {
      firstDayOfWeek: 1,
    },
    onValueUpdate: () => {
      emitPickerInputEvent(input, "input");
    },
    onChange: () => {
      emitPickerInputEvent(input, "change");
      getValidationAPI()?.validateField?.(input, { renderSummary: false });
    },
  };

  if (input.type === "time") {
    return {
      ...common,
      enableTime: true,
      noCalendar: true,
      dateFormat: "H:i",
      altInput: false,
      minTime: resolveAttr(input, "pickerMin") || input.getAttribute("min") || undefined,
      maxTime: resolveAttr(input, "pickerMax") || input.getAttribute("max") || undefined,
    };
  }

  return {
    ...common,
    enableTime: true,
    dateFormat: "Y-m-d\\TH:i",
    altInput: true,
    altFormat: "M j, Y H:i",
    minDate: resolveAttr(input, "pickerMin") || input.getAttribute("min") || undefined,
    maxDate: resolveAttr(input, "pickerMax") || input.getAttribute("max") || undefined,
  };
}

function resolveMinuteIncrement(input: HTMLInputElement): number {
  const raw = resolveAttr(input, "minuteIncrement") || input.getAttribute("step") || "";
  const step = Number.parseInt(String(raw || "").trim(), 10);
  if (!Number.isFinite(step) || step <= 0) {
    return 5;
  }
  if (step >= 60) {
    const minutes = Math.max(1, Math.round(step / 60));
    return clamp(minutes, 1, 60);
  }
  return clamp(step, 1, 60);
}

function bindExternalLabels(id: string, instance: FlatpickrInstance) {
  doc.querySelectorAll<HTMLLabelElement>(`label[for="${escapeSelectorValue(id)}"]`).forEach((label) => {
    if (label.dataset.wmuDatetimePickerLabelBound === "true") {
      return;
    }
    label.dataset.wmuDatetimePickerLabelBound = "true";
    label.addEventListener("click", (event) => {
      event.preventDefault();
      instance.open();
    });
  });
}

function emitPickerInputEvent(input: HTMLInputElement, name: "input" | "change") {
  input.dispatchEvent(new Event(name, { bubbles: true }));
}

function getValidationAPI() {
  return (window as Window & {
    WMUValidation?: {
      validateField?: (input: HTMLInputElement, options?: { renderSummary?: boolean }) => boolean;
    };
  }).WMUValidation;
}

function escapeSelectorValue(value: string) {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(value);
  }
  return value.replace(/["\\]/g, "\\$&");
}

function resolveAttr(input: HTMLInputElement, key: string): string {
  const value = input.dataset[key];
  return typeof value === "string" ? value.trim() : "";
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

if (!window.__WMU_DATETIME_PICKER_BOOTED) {
  window.__WMU_DATETIME_PICKER_BOOTED = true;
  window.WMUDateTimePicker = { init } satisfies WMUDateTimePickerAPI;
  if (doc.readyState === "loading") {
    doc.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }
}
